import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Api from "../../../utils/Api";

export const PUBLIC_ROUTES = [
  "/",
  "/welcome",
  "/safety-policy",
  "/privacy-policy",
  "/help",
];
export const LETTER_INVITE_ROUTE_PREFIXES = ["/l/", "/letter/"];
export const MEMBER_INVITE_ROUTE_PREFIX = "/i/";
export const isLetterInvitePath = (pathname = "") =>
  LETTER_INVITE_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
export const isMemberInvitePath = (pathname = "") =>
  pathname.startsWith(MEMBER_INVITE_ROUTE_PREFIX);
export const isPublicRoutePath = (pathname = "") =>
  PUBLIC_ROUTES.includes(pathname) ||
  isLetterInvitePath(pathname) ||
  isMemberInvitePath(pathname);

const LANDING_GRADIENT = "linear-gradient(135deg, #16113f 0%, #6531d7 56%, #1597d6 100%)";
const FLOAT_DURATION_MS = 3000;
const RAVEN_FRAME_DURATION_MS = 105;
const RAVEN_FLY_FRAME_COUNT = 8;
const RAVEN_FLY_COLUMNS = 3;
const RAVEN_FLY_ROWS = 3;
const RAVEN_FLY_FRAME_ASPECT = 303 / 415;
const VIMEO_PLAYER_SCRIPT_URL = "https://player.vimeo.com/api/player.js";
const LANDING_VIDEO_EMBED_URL =
  "https://player.vimeo.com/video/1197346099?title=0&byline=0&portrait=0&badge=0&autoplay=1";
const APPLE_APP_ID = "6477909689";
const APP_STORE_URL =
  "https://apps.apple.com/us/app/raven-slow-messaging/id6477909689";
const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.sloreal";
let vimeoPlayerPromise;

function getVisitorStorePlatform() {
  if (typeof navigator === "undefined") return "ios";

  const userAgent = navigator.userAgent || "";
  const platform = navigator.userAgentData?.platform || navigator.platform || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  if (/Android/i.test(userAgent) || /Android/i.test(platform)) {
    return "android";
  }

  if (
    /iPhone|iPad|iPod/i.test(userAgent) ||
    /iPhone|iPad|iPod/i.test(platform) ||
    (/Mac/i.test(platform) && maxTouchPoints > 1)
  ) {
    return "ios";
  }

  return "ios";
}

const ABOUT_INTRO_TEXT = [
  "Welcome to the very unserious slow messaging movement.",
  "Raven is for people who love to frolic and talk and also think instant delivery is a little lame.",
  "Your letter does not teleport. It gets a carrier, crosses a tiny world, and arrives with the gentle drama of something that took the scenic route on purpose.",
  "We are not mad at fast messages. Some of our best messages are fast. We just think a little wait can make a hello feel like an event.",
  "Send a note. Let it wander. Act surprised when the whole thing is charming.",
];

const ABOUT_HIGHLIGHTS = [
  {
    title: "A gentler pace",
    body: "No rush, no pressure. Just a message taking its time in the nicest possible way.",
    accentColor: "#ffd47d",
    backgroundColor: "rgba(255, 212, 125, 0.14)",
  },
  {
    title: "A tiny adventure",
    body: "Every letter gets a carrier and a route across the map before it arrives.",
    accentColor: "#9fe6d0",
    backgroundColor: "rgba(98, 214, 180, 0.14)",
  },
  {
    title: "A better entrance",
    body: "The little wait gives every hello a bit more suspense, charm, and personality.",
    accentColor: "#f4a3bd",
    backgroundColor: "rgba(244, 163, 189, 0.14)",
  },
];

const AVATAR_GRADIENTS_BY_LETTER = {
  A: ["#5B6CFF", "#27C6A5"],
  B: ["#9B5DE5", "#F15BB5"],
  C: ["#00A8A8", "#77D970"],
  D: ["#2D7DD2", "#9F7AEA"],
  E: ["#F97360", "#F9C74F"],
  F: ["#3A86FF", "#4CC9F0"],
  G: ["#6D597A", "#E56B6F"],
  H: ["#118AB2", "#06D6A0"],
  I: ["#4361EE", "#F72585"],
  J: ["#277DA1", "#90BE6D"],
  K: ["#7209B7", "#4CC9F0"],
  L: ["#38BDF8", "#34D399"],
  M: ["#60A5FA", "#A78BFA"],
  N: ["#0077B6", "#00B4D8"],
  O: ["#8338EC", "#FB5607"],
  P: ["#264653", "#2A9D8F"],
  Q: ["#EF476F", "#FFD166"],
  R: ["#F472B6", "#FB7185"],
  S: ["#560BAD", "#B5179E"],
  T: ["#FDE047", "#FB923C"],
  U: ["#3A0CA3", "#4895EF"],
  V: ["#0F766E", "#84CC16"],
  W: ["#7C3AED", "#06B6D4"],
  X: ["#DC2626", "#F59E0B"],
  Y: ["#1D4ED8", "#10B981"],
  Z: ["#8B5CF6", "#EC4899"],
};
const AVATAR_GRADIENTS = Object.values(AVATAR_GRADIENTS_BY_LETTER);

const LETTER_INVITE_DEFAULT_HEADER = "A little note is already on its way";
const LETTER_INVITE_DEFAULT_MESSAGE = "A new letter is waiting for you.";
const LETTER_INVITE_LOADING_MESSAGE = "Fetching the real letter from Raven.";
const LETTER_INVITE_ERROR_MESSAGE =
  "This invite may have expired or the letter may no longer be available.";
const INVITE_TEXT_CLIP_BASE_HEIGHT = 245;
const INVITE_TEXT_CLIP_WITH_FILES_HEIGHT = 175;
const INVITE_TEXT_EXTRA_HEIGHT_THRESHOLD = 110;
const INVITE_TEXT_EXTRA_HEIGHT_RATIO = 0.55;
const INVITE_TEXT_MAX_EXTRA_HEIGHT = 78;
const INVITE_TEXT_MAX_EXTRA_HEIGHT_WITH_FILES = 54;

function usePageTitle(title) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

function useViewport() {
  const getViewport = () => ({
    width: typeof window === "undefined" ? 1200 : window.innerWidth,
    height: typeof window === "undefined" ? 800 : window.innerHeight,
  });
  const [viewport, setViewport] = useState(getViewport);

  useEffect(() => {
    const updateViewport = () => setViewport(getViewport());
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  return {
    ...viewport,
    isNarrow: viewport.width < 860,
    isMobile: viewport.width < 620,
  };
}

function useAppleSmartAppBanner() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    let meta = document.querySelector('meta[name="apple-itunes-app"]');
    const previousContent = meta?.getAttribute("content");
    const createdMeta = !meta;

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "apple-itunes-app");
      document.head.appendChild(meta);
    }

    meta.setAttribute("content", `app-id=${APPLE_APP_ID}`);

    return () => {
      if (createdMeta) {
        meta.remove();
      } else if (previousContent) {
        meta.setAttribute("content", previousContent);
      } else {
        meta.removeAttribute("content");
      }
    };
  }, []);
}

function PublicShell({ children, variant = "default" }) {
  const { isMobile } = useViewport();

  return (
    <main
      style={{
        ...styles.publicPage,
        ...(variant === "policy" ? styles.policyPage : {}),
      }}
    >
      {children}
      <footer style={{ ...styles.footer, ...(isMobile ? styles.footerMobile : {}) }}>
        <span>Raven</span>
        <a style={styles.footerLink} href="mailto:jack@sendaraven.io">
          jack@sendaraven.io
        </a>
      </footer>
    </main>
  );
}

function LandingLink({ to, label, tone = "primary", style }) {
  return (
    <Link style={{ ...styles.landingLink, ...styles[`${tone}Link`], ...style }} to={to}>
      {label}
    </Link>
  );
}

function getAvatarGradient(name) {
  const normalizedName = (name || "A").trim().toUpperCase();
  const nameHash = normalizedName.split("").reduce((acc, char, index) => {
    return acc + char.charCodeAt(0) * (index + 1);
  }, 0);
  return AVATAR_GRADIENTS[nameHash % AVATAR_GRADIENTS.length];
}

function SenderAvatar({ imageUrl, name, size = 42, style }) {
  const [didImageFail, setDidImageFail] = useState(false);
  const fallbackName = name || "A";
  const firstLetter = fallbackName.charAt(0).toUpperCase();
  const gradient = getAvatarGradient(fallbackName);
  const shouldShowImage = !!imageUrl && !didImageFail;

  useEffect(() => {
    setDidImageFail(false);
  }, [imageUrl]);

  return (
    <span
      style={{
        ...styles.senderAvatar,
        width: size,
        height: size,
        borderRadius: size / 2,
        background: `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 100%)`,
        ...style,
      }}
    >
      {shouldShowImage ? (
        <img
          alt=""
          aria-hidden="true"
          onError={() => setDidImageFail(true)}
          src={imageUrl}
          style={styles.senderAvatarImage}
        />
      ) : (
        <span
          aria-hidden="true"
          style={{
            ...styles.senderAvatarInitial,
            fontSize: Math.max(14, Math.round(size * 0.36)),
          }}
        >
          {firstLetter}
        </span>
      )}
    </span>
  );
}

function AppleStoreIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" style={styles.storeIconSvg}>
      <path
        d="M16.7 12.4c0-2.7 2.2-4 2.3-4.1-1.3-1.8-3.2-2.1-3.9-2.1-1.6-.2-3.2 1-4 1-0.8 0-2.1-1-3.5-.9-1.8 0-3.4 1-4.3 2.6-1.8 3.1-.5 7.8 1.3 10.3.9 1.2 1.9 2.6 3.3 2.5 1.3-.1 1.8-.8 3.4-.8 1.6 0 2.1.8 3.5.8 1.5 0 2.4-1.3 3.2-2.5 1-1.4 1.4-2.8 1.4-2.9 0 0-2.7-1-2.7-3.9zM14.1 4.6c.7-.8 1.1-1.9 1-3-.9 0-2 .6-2.7 1.4-.6.7-1.1 1.9-1 2.9 1 .1 2-.5 2.7-1.3z"
        fill="currentColor"
      />
    </svg>
  );
}

function GooglePlayStoreIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 28 30" style={styles.storeIconSvg}>
      <path d="M2.3 1.4 17 15 2.3 28.6c-.4-.4-.6-1-.6-1.8V3.2c0-.8.2-1.4.6-1.8z" fill="#2fd1ff" />
      <path d="m17 15 4.4 4.1L5.2 28.4c-1.1.6-2.1.7-2.9.2L17 15z" fill="#00d47e" />
      <path d="m17 15 4.4-4.1 4.2 2.4c1.5.9 1.5 2.5 0 3.4l-4.2 2.4L17 15z" fill="#ffd34d" />
      <path d="M5.2 1.6 21.4 11 17 15 2.3 1.4c.8-.5 1.8-.4 2.9.2z" fill="#ff5b5f" />
    </svg>
  );
}

function StoreIcon({ store }) {
  return (
    <span style={styles.storeIconWrap}>
      {store === "google" ? <GooglePlayStoreIcon /> : <AppleStoreIcon />}
    </span>
  );
}

function StoreLink({ href, eyebrow, label, store, tone = "primary", style }) {
  return (
    <a
      href={href}
      rel="noopener noreferrer"
      style={{ ...styles.storeLink, ...styles[`${tone}StoreLink`], ...style }}
      target="_blank"
    >
      <StoreIcon store={store} />
      <span style={styles.storeLinkText}>
        <span style={styles.storeLinkEyebrow}>{eyebrow}</span>
        <span style={styles.storeLinkLabel}>{label}</span>
      </span>
    </a>
  );
}

function getValue(object, camelKey, snakeKey) {
  if (!object) return undefined;
  if (object[camelKey] !== undefined && object[camelKey] !== null) {
    return object[camelKey];
  }
  return object[snakeKey];
}

function normalizeInviteFile(file) {
  const fileType = `${getValue(file, "fileType", "file_type") || ""}`.toLowerCase();
  const mediaType = fileType.split("/")[0];

  return {
    url: getValue(file, "url", "url"),
    fileType,
    mediaType,
    width: getValue(file, "width", "width"),
    height: getValue(file, "height", "height"),
  };
}

function normalizeLetterInvite(payload) {
  const rawFiles = getValue(payload, "files", "files") || [];

  return {
    senderName: getValue(payload, "senderMemberName", "sender_member_name") || "",
    senderImageUrl:
      getValue(payload, "senderMemberImageUrl", "sender_member_image_url") || "",
    header: getValue(payload, "header", "header") || "",
    message: getValue(payload, "message", "message") || "",
    hasHiddenMessage: !!getValue(
      payload,
      "hasHiddenMessage",
      "has_hidden_message"
    ),
    files: Array.isArray(rawFiles)
      ? rawFiles
          .map(normalizeInviteFile)
          .filter((file) => file.url && file.mediaType)
      : [],
  };
}

function getInviteParagraphs(message) {
  return `${message || LETTER_INVITE_DEFAULT_MESSAGE}`
    .split(/\n+/)
    .map((text) => text.trim())
    .filter(Boolean)
    .slice(0, 4);
}

function getInviteTextClipStyle(previewParagraphs, hasFiles) {
  const previewLength = previewParagraphs.join(" ").length;
  const baseHeight = hasFiles
    ? INVITE_TEXT_CLIP_WITH_FILES_HEIGHT
    : INVITE_TEXT_CLIP_BASE_HEIGHT;

  if (previewLength <= INVITE_TEXT_EXTRA_HEIGHT_THRESHOLD) {
    return { maxHeight: baseHeight };
  }

  const maxExtraHeight = hasFiles
    ? INVITE_TEXT_MAX_EXTRA_HEIGHT_WITH_FILES
    : INVITE_TEXT_MAX_EXTRA_HEIGHT;
  const extraHeight = Math.min(
    maxExtraHeight,
    Math.round(
      (previewLength - INVITE_TEXT_EXTRA_HEIGHT_THRESHOLD) *
        INVITE_TEXT_EXTRA_HEIGHT_RATIO
    )
  );

  return { maxHeight: baseHeight + extraHeight };
}

function LetterInviteFiles({ files }) {
  if (!files.length) return null;

  return (
    <div style={styles.inviteLetterFiles}>
      {files.map((file, index) => (
        <LetterInviteFile
          file={file}
          key={`${file.mediaType}-${file.url}-${index}`}
        />
      ))}
    </div>
  );
}

function LetterInviteFile({ file }) {
  const aspectRatio =
    file.width && file.height ? `${file.width} / ${file.height}` : undefined;
  const mediaStyle = {
    ...styles.inviteLetterMedia,
    ...(aspectRatio ? { aspectRatio } : {}),
  };

  if (file.mediaType === "image") {
    return (
      <div style={styles.inviteLetterFile}>
        <span style={styles.inviteLetterFileLabel}>Image</span>
        <img alt="" src={file.url} style={mediaStyle} />
      </div>
    );
  }

  if (file.mediaType === "video") {
    return (
      <div style={styles.inviteLetterFile}>
        <span style={styles.inviteLetterFileLabel}>Video</span>
        <video
          controls
          playsInline
          preload="metadata"
          src={file.url}
          style={mediaStyle}
        />
      </div>
    );
  }

  if (file.mediaType === "audio") {
    return (
      <div style={styles.inviteLetterFile}>
        <span style={styles.inviteLetterFileLabel}>Audio</span>
        <div style={styles.inviteLetterAudioTile}>
          <audio controls preload="metadata" src={file.url} style={styles.inviteLetterAudio} />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.inviteLetterFile}>
      <a href={file.url} rel="noopener noreferrer" style={styles.inviteLetterFileLink} target="_blank">
        Open attachment
      </a>
    </div>
  );
}

function loadVimeoPlayer() {
  if (typeof window === "undefined") return Promise.reject();
  if (window.Vimeo?.Player) return Promise.resolve(window.Vimeo.Player);
  if (vimeoPlayerPromise) return vimeoPlayerPromise;

  vimeoPlayerPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      `script[src="${VIMEO_PLAYER_SCRIPT_URL}"]`,
    );

    const handleLoad = () => resolve(window.Vimeo.Player);
    const handleError = () => reject(new Error("Unable to load Vimeo player"));

    if (existingScript) {
      existingScript.addEventListener("load", handleLoad, { once: true });
      existingScript.addEventListener("error", handleError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = VIMEO_PLAYER_SCRIPT_URL;
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
    document.body.appendChild(script);
  });

  return vimeoPlayerPromise;
}

function useSmoothFloat(ref, { amplitude = 9, phase = 0, rotate = "" }) {
  useEffect(() => {
    let animationFrameId;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const offset =
        Math.sin((elapsed / FLOAT_DURATION_MS) * Math.PI * 2 + phase) *
        amplitude;

      if (ref.current) {
        ref.current.style.transform = `translate3d(0, ${offset}px, 0) ${rotate}`;
      }

      animationFrameId = requestAnimationFrame(tick);
    };

    animationFrameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrameId);
  }, [amplitude, phase, ref, rotate]);
}

function RavenSprite({ size = 320, style, amplitude = 9 }) {
  const ref = useRef(null);
  const height = Math.round(size * RAVEN_FLY_FRAME_ASPECT);

  useEffect(() => {
    let animationFrameId;
    let lastFrame = -1;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const frame =
        Math.floor(elapsed / RAVEN_FRAME_DURATION_MS) %
        RAVEN_FLY_FRAME_COUNT;
      const floatOffset =
        Math.sin((elapsed / FLOAT_DURATION_MS) * Math.PI * 2) * amplitude;

      if (ref.current) {
        ref.current.style.transform = `translate3d(0, ${floatOffset}px, 0)`;

        if (frame !== lastFrame) {
          const column = frame % RAVEN_FLY_COLUMNS;
          const row = Math.floor(frame / RAVEN_FLY_COLUMNS);
          ref.current.style.backgroundPosition = `-${column * size}px -${
            row * height
          }px`;
          lastFrame = frame;
        }
      }

      animationFrameId = requestAnimationFrame(tick);
    };

    animationFrameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrameId);
  }, [amplitude, height, size]);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      style={{
        width: size,
        height,
        backgroundImage: "url('/raven-assets/raven_fly.png')",
        backgroundRepeat: "no-repeat",
        backgroundSize: `${size * RAVEN_FLY_COLUMNS}px ${height * RAVEN_FLY_ROWS}px`,
        backgroundPosition: "0px 0px",
        filter: "drop-shadow(0 18px 22px rgba(0,0,0,0.28))",
        transform: "translate3d(0, 0, 0)",
        willChange: "transform, background-position",
        ...style,
      }}
    />
  );
}

function SceneBand({ style }) {
  return <div aria-hidden="true" style={{ ...styles.sceneBand, ...style }} />;
}

function ColorTrail({ style, amplitude = 9, phase = 0, rotate = "-10deg" }) {
  const ref = useRef(null);
  useSmoothFloat(ref, { amplitude, phase, rotate: `rotate(${rotate})` });

  return (
    <div
      ref={ref}
      aria-hidden="true"
      style={{
        ...styles.colorTrail,
        transform: `translate3d(0, 0, 0) rotate(${rotate})`,
        ...style,
      }}
    />
  );
}

export function LandingPageView() {
  usePageTitle("Raven: Incredibly Slow Messaging");
  const { isNarrow, isMobile, height } = useViewport();
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoIframeRef = useRef(null);
  const vimeoPlayerRef = useRef(null);
  const spriteSize = isMobile ? 156 : isNarrow ? 260 : 340;
  const heroMinHeight = isMobile ? 520 : Math.max(620, height - 146);

  useEffect(() => {
    if (!isVideoLoaded) return undefined;
    let isMounted = true;

    const handleVideoEnded = () => {
      if (isMounted) {
        setIsVideoLoaded(false);
      }
    };
    const handleTimeUpdate = ({ duration, seconds }) => {
      if (duration && duration - seconds <= 0.35) {
        handleVideoEnded();
      }
    };

    loadVimeoPlayer()
      .then((Player) => {
        if (!isMounted || !videoIframeRef.current) return;

        const player = new Player(videoIframeRef.current);
        vimeoPlayerRef.current = player;
        player.on("ended", handleVideoEnded);
        player.on("timeupdate", handleTimeUpdate);
      })
      .catch(() => {});

    return () => {
      isMounted = false;
      if (vimeoPlayerRef.current) {
        vimeoPlayerRef.current.off("ended", handleVideoEnded);
        vimeoPlayerRef.current.off("timeupdate", handleTimeUpdate);
        vimeoPlayerRef.current = null;
      }
    };
  }, [isVideoLoaded]);

  return (
    <PublicShell>
      <section
        style={{
          ...styles.hero,
          minHeight: heroMinHeight,
          ...(isNarrow ? styles.heroNarrow : {}),
          ...(isMobile ? styles.heroMobile : {}),
        }}
      >
        <div style={styles.heroScene}>
          {!isMobile && (
            <>
              <SceneBand style={styles.sceneBandOne} />
              <SceneBand style={styles.sceneBandTwo} />
              <ColorTrail style={styles.trailOne} phase={0.25} />
              <ColorTrail style={styles.trailTwo} amplitude={5} phase={1.3} />
              <ColorTrail style={styles.trailThree} amplitude={5} phase={2.1} />
            </>
          )}
          <RavenSprite
            size={spriteSize}
            style={{
              ...styles.heroRaven,
              ...(isNarrow ? styles.heroRavenNarrow : {}),
              ...(isMobile ? styles.heroRavenMobile : {}),
            }}
          />
        </div>

        <div style={{ ...styles.heroContent, ...(isNarrow ? styles.heroContentNarrow : {}) }}>
          <h1 style={{ ...styles.heroTitle, ...(isMobile ? styles.heroTitleMobile : {}) }}>Raven</h1>
          <p style={{ ...styles.heroSubtitle, ...(isMobile ? styles.heroSubtitleMobile : {}) }}>
            Incredibly Slow Messaging
          </p>
          <p style={{ ...styles.heroCopy, ...(isMobile ? styles.heroCopyMobile : {}) }}>
            Send letters, photos, and videos through carriers with a little more
            personality than a regular text.
          </p>
          <div
            style={{
              ...styles.storeLinkStack,
              ...(isMobile ? styles.storeLinkStackMobile : {}),
            }}
          >
            <StoreLink
              eyebrow="Download on the"
              href={APP_STORE_URL}
              label="App Store"
              store="apple"
            />
            <StoreLink
              eyebrow="Get it on"
              href={GOOGLE_PLAY_URL}
              label="Google Play"
              store="google"
              tone="secondary"
            />
          </div>
        </div>
      </section>

      <section style={{ ...styles.section, ...(isMobile ? styles.sectionMobile : {}) }}>
        <div style={{ ...styles.videoGrid, ...(isNarrow ? styles.oneColumnGrid : {}) }}>
          <div style={styles.videoCopy}>
            <p style={styles.kicker}>Raven in motion</p>
            <h2 style={{ ...styles.sectionTitle, ...(isMobile ? styles.sectionTitleMobile : {}) }}>
              A tiny world built for messages that take their time.
            </h2>
            <p style={styles.videoText}>
              Letters travel by carrier, cross the map, and arrive with a
              little flourish instead of another instant ping.
            </p>
          </div>
          <div style={{ ...styles.videoFrame, ...(isMobile ? styles.videoFrameMobile : {}) }}>
            {isVideoLoaded ? (
              <iframe
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                allowFullScreen
                loading="lazy"
                ref={videoIframeRef}
                src={LANDING_VIDEO_EMBED_URL}
                style={styles.videoIframe}
                title="Raven video"
              />
            ) : (
              <button
                aria-label="Play Raven video"
                onClick={() => setIsVideoLoaded(true)}
                style={styles.videoPlaceholder}
                type="button"
              >
                <img
                  alt=""
                  aria-hidden="true"
                  src="/raven-assets/app-icon.png"
                  style={styles.videoPlaceholderIcon}
                />
                <span style={styles.videoPlayButton}>
                  <span style={styles.videoPlayTriangle} />
                </span>
                <span style={styles.videoPlaceholderTitle}>Watch Raven</span>
              </button>
            )}
          </div>
        </div>
      </section>

      <section style={{ ...styles.section, ...(isMobile ? styles.sectionMobile : {}) }}>
        <div style={{ ...styles.aboutGrid, ...(isNarrow ? styles.oneColumnGrid : {}) }}>
          <div style={styles.aboutIntro}>
            <p style={styles.kicker}>About Raven</p>
            {ABOUT_INTRO_TEXT.map((text, idx) =>
              idx === 0 ? (
                <h2
                  key={text}
                  style={{
                    ...styles.aboutLead,
                    ...(isMobile ? styles.aboutLeadMobile : {}),
                  }}
                >
                  {text}
                </h2>
              ) : (
                <p key={text} style={styles.aboutText}>
                  {text}
                </p>
              ),
            )}
          </div>
          <div style={styles.aboutHighlightStack}>
            {ABOUT_HIGHLIGHTS.map((highlight) => (
              <AboutHighlightCard key={highlight.title} {...highlight} />
            ))}
          </div>
        </div>
      </section>

      <section style={{ ...styles.section, ...styles.infoBand, ...(isNarrow ? styles.infoBandNarrow : {}) }}>
        <div>
          <p style={styles.kicker}>Useful pages</p>
          <h2 style={{ ...styles.sectionTitle, ...(isMobile ? styles.sectionTitleMobile : {}) }}>
            Policies and support in one place.
          </h2>
        </div>
        <div style={styles.policyLinkGrid}>
          <LandingLink to="/safety-policy" label="Safety Policy" />
          <LandingLink to="/privacy-policy" label="Privacy Policy" />
          <LandingLink to="/help" label="Help" />
        </div>
      </section>
    </PublicShell>
  );
}

function AboutHighlightCard({ title, body, accentColor, backgroundColor }) {
  return (
    <article style={{ ...styles.aboutHighlightCard, borderLeftColor: accentColor }}>
      <div style={{ ...styles.aboutHighlightMark, backgroundColor }}>
        <span style={{ ...styles.aboutHighlightDot, backgroundColor: accentColor }} />
      </div>
      <div style={styles.aboutHighlightCopy}>
        <h3 style={styles.featureTitle}>{title}</h3>
        <p style={styles.bodyText}>{body}</p>
      </div>
    </article>
  );
}

function PolicyLayout({ title, eyebrow, intro, children }) {
  const { isMobile } = useViewport();

  return (
    <PublicShell variant="policy">
      <section style={{ ...styles.policyHero, ...(isMobile ? styles.policyHeroMobile : {}) }}>
        <p style={styles.kicker}>{eyebrow}</p>
        <h1 style={{ ...styles.policyTitle, ...(isMobile ? styles.policyTitleMobile : {}) }}>{title}</h1>
        {intro && <p style={{ ...styles.policyIntro, ...(isMobile ? styles.policyIntroMobile : {}) }}>{intro}</p>}
      </section>
      <section style={{ ...styles.policyContent, ...(isMobile ? styles.policyContentMobile : {}) }}>{children}</section>
    </PublicShell>
  );
}

function PolicySection({ number, title, children }) {
  const { isMobile } = useViewport();

  return (
    <article style={{ ...styles.policySection, ...(isMobile ? styles.policySectionMobile : {}) }}>
      <div style={styles.policySectionLabel}>{number}</div>
      <div>
        <h2 style={styles.policySectionTitle}>{title}</h2>
        {children}
      </div>
    </article>
  );
}

function P({ children }) {
  return <p style={styles.policyText}>{children}</p>;
}

function PolicySubheading({ children }) {
  return <h3 style={styles.policySubheading}>{children}</h3>;
}

function BulletList({ items }) {
  return (
    <ul style={styles.bulletList}>
      {items.map((item) => (
        <li key={item} style={styles.policyListItem}>
          {item}
        </li>
      ))}
    </ul>
  );
}

export function SafetyPolicyView() {
  usePageTitle("Safety Policy | Raven");

  return (
    <PolicyLayout
      eyebrow="CSAM prevention and compliance"
      title="Safety Policy"
      intro="At Raven, we are committed to user safety and to the prevention of child exploitation on our platform. While our app enables private sharing of messages, images, and videos between users, we retain the technical ability to access this content when legally or ethically necessary to enforce safety policies."
    >
      <PolicySection number="01" title="Zero-Tolerance Policy">
        <P>
          Raven has a strict zero-tolerance policy toward Child Sexual Abuse
          Material (CSAM). The sharing, solicitation, or possession of CSAM is
          illegal and strictly prohibited on our platform. We report all
          confirmed violations to appropriate authorities and permanently ban
          offending accounts.
        </P>
      </PolicySection>

      <PolicySection number="02" title="Private Messaging and Media">
        <P>
          User communications on Raven are designed to be private between sender
          and recipient. However, to comply with legal obligations and safety
          standards, we retain the technical capability to access content when
          required by:
        </P>
        <BulletList items={["User reports", "Legal requests", "Safety investigations"]} />
        <P>
          Access to such content is tightly controlled and limited to trained
          personnel for specific abuse-related cases.
        </P>
      </PolicySection>

      <PolicySection number="03" title="Detection and Moderation Practices">
        <BulletList
          items={[
            "Proactive monitoring: Where appropriate and legally permitted, we may use automated tools such as hash-matching like PhotoDNA to detect known CSAM in uploaded media.",
            "Reactive investigation: We investigate reports of abuse and may access user content as part of that process.",
            "Manual review: Trained staff review flagged content and escalate as needed.",
          ]}
        />
      </PolicySection>

      <PolicySection number="04" title="Reporting and User Tools">
        <P>Users can report suspicious or harmful behavior through:</P>
        <BulletList items={["In-app report tools in chat or user profiles", "Direct contact via email: jack@sendaraven.io"]} />
        <P>
          All reports are reviewed promptly and confidentially. We prioritize
          user safety while respecting privacy to the fullest extent possible.
        </P>
      </PolicySection>

      <PolicySection number="05" title="Law Enforcement Cooperation">
        <P>
          Raven cooperates fully with local and international law enforcement
          agencies and child protection organizations. We report verified CSAM to
          agencies such as:
        </P>
        <BulletList
          items={[
            "National Center for Missing and Exploited Children (NCMEC)",
            "Other appropriate legal bodies in accordance with jurisdiction",
          ]}
        />
      </PolicySection>

      <PolicySection number="06" title="Age Restrictions and Child Safety">
        <P>To protect minors:</P>
        <BulletList
          items={[
            "Users must be 13 or older to use Raven",
            "We may restrict or remove access to users believed to be underage",
            "Additional safeguards may be applied to high-risk features or content-sharing behavior",
          ]}
        />
      </PolicySection>

      <PolicySection number="07" title="Data Retention and Compliance">
        <BulletList
          items={[
            "We store user content securely in accordance with our privacy policy",
            "Metadata and abuse report records are retained to support investigations and compliance",
            "We follow global safety and privacy laws, including COPPA, GDPR, and regional CSAM regulations",
          ]}
        />
      </PolicySection>

      <PolicySection number="08" title="Continuous Review and Improvement">
        <P>
          Raven regularly evaluates and improves its safety and moderation
          systems. We stay informed about evolving threats and work to strengthen
          protections for vulnerable users while respecting the privacy and
          intent of our platform.
        </P>
      </PolicySection>

      <div style={styles.contactPanel}>
        <h2 style={styles.contactPanelTitle}>Contact and Reporting</h2>
        <a style={styles.inlineLink} href="mailto:jack@sendaraven.io">
          jack@sendaraven.io
        </a>
      </div>
    </PolicyLayout>
  );
}

export function PrivacyPolicyView() {
  usePageTitle("Privacy Policy | Raven");

  return (
    <PolicyLayout
      eyebrow="Privacy"
      title="Privacy Policy"
      intro="This Privacy Policy explains how The Raven Company (Raven, we, or us) collects, uses, shares, retains, and deletes information when you use the Raven mobile application. Raven asks for permission before accessing protected data on your device."
    >
      <PolicySection number="01" title="Information We Collect">
        <P>
          Account and profile information may include your name, phone number,
          profile image, age-confirmation information, account identifiers,
          preferences, and information you provide when contacting support.
        </P>
        <P>
          User content may include letters, attachments, profile content,
          reports, and other information you choose to create or send through
          Raven. We also collect information needed to operate the Service,
          such as app version, device and push-notification identifiers,
          language, purchase records, feature activity, and diagnostics.
        </P>
        <P>
          Purchases are processed by Apple or Google. Raven receives the
          transaction information needed to verify and provide a purchase, but
          does not receive or store your full payment-card details.
        </P>
      </PolicySection>

      <PolicySection number="02" title="Contacts">
        <P>
          Contact access is optional. If you grant Contacts permission, Raven
          uploads only names, first names, and normalized phone numbers from the
          contacts you allow to Raven's servers. Raven does not upload contact
          photos, email addresses, street addresses, notes, or other contact
          fields.
        </P>
        <BulletList
          items={[
            "We use uploaded contacts to find people you know who are already on Raven.",
            "We use them to help you choose letter recipients and suggest people you may decide to invite.",
            "We may use matching, mutual-connection, name-quality, and contact-popularity signals to rank those suggestions.",
            "Raven does not automatically message or invite your contacts, sell uploaded contacts, or use them for advertising.",
          ]}
        />
        <P>
          Uploaded contacts are associated with your Raven account. Large
          contact-sync payloads may be held temporarily in a processing queue;
          the queued contact payload is cleared after successful processing.
        </P>
      </PolicySection>

      <PolicySection number="03" title="Location and Map Information">
        <P>
          Raven uses location to create your Castle, route letters, estimate
          delivery time, display courier journeys on the World map, and support
          location-based collections such as stamps. You may enter a Castle
          location manually. If you choose to use your device's current
          location, Raven sends its coordinates to our servers after you grant
          location permission.
        </P>
        <P>
          A Castle is a saved delivery location, not continuous live-location
          tracking. If you enable automatic Castle updates, Raven may refresh
          that saved location when you open the app while location permission is
          granted. Raven does not collect location continuously in the
          background.
        </P>
        <P>
          Castle markers and courier routes may be visible to members Raven
          treats as your friends, including relationships created through
          contact discovery. Raven does not display your Castle to the public or
          to nearby strangers. Friend Mode may show your active couriers to
          friends without revealing the recipient. PenPals and other strangers
          see an obscured PenPal Post instead of your Castle location.
        </P>
      </PolicySection>

      <PolicySection number="04" title="How We Use Information">
        <BulletList
          items={[
            "To create and maintain accounts and authenticate members.",
            "To provide letters, courier routing, friend discovery, PenPal matching, notifications, purchases, and customer support.",
            "To protect members, investigate reports, prevent abuse and fraud, and enforce our terms and safety policies.",
            "To diagnose problems, understand aggregate product usage, and improve Raven.",
            "To comply with legal obligations and respond to lawful requests.",
          ]}
        />
      </PolicySection>

      <PolicySection number="05" title="How We Share Information">
        <P>
          We share information with other members only as needed for the Raven
          features you use, such as your profile, letters, friend relationships,
          Castle or obscured PenPal Post, and courier journeys. The information
          visible depends on the relationship and feature involved.
        </P>
        <P>
          We use service providers to host, secure, analyze, and operate Raven.
          They may process information only to provide services to Raven and
          must protect it consistently with this policy and applicable law. We
          may also disclose information when required by law or when reasonably
          necessary to protect members, Raven, or others. We do not sell personal
          information or uploaded contacts.
        </P>
      </PolicySection>

      <PolicySection number="06" title="Retention and Deletion">
        <P>
          We retain account information, uploaded contacts, saved Castle
          information, and user content while your account is active or as
          needed to provide Raven. Some operational, security, fraud-prevention,
          dispute, backup, and legal records may be retained for a limited period
          when reasonably necessary.
        </P>
        <P>
          You can delete your account from Account Management in the app. Account
          deletion deletes your account and associated contact-sync records,
          subject to the limited retention described above. To request deletion
          of previously uploaded contacts without deleting your account, contact
          us at jack@sendaraven.io.
        </P>
      </PolicySection>

      <PolicySection number="07" title="Your Choices and Consent">
        <BulletList
          items={[
            "You may decline Contacts or Location permission and continue using features that do not require that data.",
            "You can revoke device permissions at any time in your device settings. Revoking Contacts permission prevents future device contact access and uploads but does not by itself delete contacts previously uploaded to Raven.",
            "You can disable automatic Castle updates in Raven and can disable device location access in your device settings.",
            "You can disable Friend Mode, archive a friend, or block a member using Raven's in-app controls.",
            "You may request access to or deletion of your personal information by contacting us.",
          ]}
        />
      </PolicySection>

      <PolicySection number="08" title="Security, Children, and Changes">
        <P>
          We use reasonable administrative, technical, and organizational
          safeguards to protect personal information. No method of electronic
          transmission or storage is completely secure, so we cannot guarantee
          absolute security.
        </P>
        <P>
          Raven is not available to children under 13. We may update this Privacy
          Policy as Raven or applicable requirements change. We will post the
          updated policy here and provide additional notice when required.
        </P>
      </PolicySection>

      <PolicySection number="09" title="Contact Us">
        <P>
          If you have questions about this Privacy Policy, our data practices,
          or a privacy request, contact us at{" "}
          <a style={styles.inlineLink} href="mailto:jack@sendaraven.io">
            jack@sendaraven.io
          </a>
          .
        </P>
      </PolicySection>
    </PolicyLayout>
  );
}

export function HelpPageView() {
  usePageTitle("Help | Raven");

  return (
    <PolicyLayout
      eyebrow="Support"
      title="Help"
      intro="Need to remove account data, delete a letter, or block someone? These are the current Raven support paths."
    >
      <PolicySection number="01" title="Account Deletion">
        <P>
          For account deletion, please send a message on the Raven app to your
          local Nest requesting deletion or email{" "}
          <a style={styles.inlineLink} href="mailto:jack@sendaraven.io">
            jack@sendaraven.io
          </a>{" "}
          with the subject line <strong>DELETE [phone_number]</strong>.
        </P>
        <P>
          This will require an extra step where we ask you to verify your phone
          number by sending a message. This will permanently delete all user data
          and correspondence between you and your friends.
        </P>
      </PolicySection>

      <PolicySection number="02" title="Delete Letters">
        <P>
          To delete letters, navigate within the app to the Letters tab, open
          the letter in the chat conversation, click the dots icon, then click
          Delete Letter.
        </P>
      </PolicySection>

      <PolicySection number="03" title="Block Friends">
        <P>
          To block friends, navigate within the app to the Letters tab, open
          your conversation with the friend, click the dots icon, then click
          Block.
        </P>
      </PolicySection>
    </PolicyLayout>
  );
}

export function MemberInvitePageView({ match }) {
  usePageTitle("Become Friends on Raven");
  const { isMobile, height } = useViewport();
  const inviteToken = match?.params?.inviteToken;
  const [loadStatus, setLoadStatus] = useState("loading");
  const isAndroid = getVisitorStorePlatform() === "android";
  const primaryStoreUrl = isAndroid ? GOOGLE_PLAY_URL : APP_STORE_URL;
  const primaryStoreLabel = isAndroid ? "Google Play" : "App Store";
  const primaryStore = isAndroid ? "google" : "apple";
  const primaryStoreEyebrow = isAndroid ? "Get it on" : "Download on the";
  const secondaryStoreUrl = isAndroid ? APP_STORE_URL : GOOGLE_PLAY_URL;
  const secondaryStoreLabel = isAndroid ? "App Store" : "Google Play";
  const secondaryStore = isAndroid ? "apple" : "google";
  const secondaryStoreEyebrow = isAndroid ? "Download on the" : "Get it on";
  const inviteMinHeight = Math.max(isMobile ? 610 : 620, height - 108);
  const isUnavailable = loadStatus === "unavailable";
  const title = isUnavailable
    ? "This Raven invite has flown away"
    : "A Raven friendship is waiting";
  const kicker = isUnavailable ? "Invite unavailable" : "Become friends on Raven";
  const body = isUnavailable
    ? "This invite link has been disabled. Ask your friend to share a new one."
    : "Download Raven to accept the invite and start sending letters that take the scenic route.";

  useAppleSmartAppBanner();

  useEffect(() => {
    let isCurrent = true;

    if (!inviteToken) {
      setLoadStatus("unavailable");
      return undefined;
    }

    setLoadStatus("loading");
    Api.get(`/member_invite_links/${encodeURIComponent(inviteToken)}`, {
      shouldLog: false,
    })
      .then((response) => {
        if (!isCurrent) return;
        setLoadStatus(
          response?.invite?.status === "available"
            ? "available"
            : "unavailable"
        );
      })
      .catch(() => {
        if (isCurrent) setLoadStatus("unavailable");
      });

    return () => {
      isCurrent = false;
    };
  }, [inviteToken]);

  return (
    <PublicShell>
      <section
        style={{
          ...styles.letterInviteHero,
          minHeight: inviteMinHeight,
          ...(isMobile ? styles.memberInviteHeroMobile : {}),
        }}
      >
        <div aria-hidden="true" style={styles.letterInviteScene}>
          <SceneBand
            style={{
              ...styles.inviteSceneBandOne,
              ...(isMobile ? styles.inviteSceneBandOneMobile : {}),
            }}
          />
          <SceneBand style={styles.inviteSceneBandTwo} />
          <ColorTrail style={styles.inviteTrailOne} amplitude={5} phase={0.7} />
          <ColorTrail style={styles.inviteTrailTwo} amplitude={4} phase={1.8} />
        </div>

        <div style={styles.memberInviteContent}>
          <RavenSprite
            size={isMobile ? 112 : 158}
            style={styles.memberInviteRaven}
            amplitude={isMobile ? 5 : 7}
          />
          <img
            alt=""
            aria-hidden="true"
            src="/raven-assets/app-icon.png"
            style={styles.inviteAppIcon}
          />
          <p style={styles.kicker}>{kicker}</p>
          <h1
            style={{
              ...styles.memberInviteTitle,
              ...(isMobile ? styles.memberInviteTitleMobile : {}),
            }}
          >
            {title}
          </h1>
          {!isUnavailable && (
            <p style={styles.memberInviteDescription}>
              Send incredibly slow letters
            </p>
          )}
          <p
            style={{
              ...styles.memberInviteBody,
              ...(isMobile ? styles.memberInviteBodyMobile : {}),
            }}
          >
            {body}
          </p>

          <div
            style={{
              ...styles.storeLinkStack,
              ...styles.memberInviteStoreLinks,
              ...(isMobile ? styles.storeLinkStackMobile : {}),
            }}
          >
            <StoreLink
              eyebrow={primaryStoreEyebrow}
              href={primaryStoreUrl}
              label={primaryStoreLabel}
              store={primaryStore}
            />
            <StoreLink
              eyebrow={secondaryStoreEyebrow}
              href={secondaryStoreUrl}
              label={secondaryStoreLabel}
              store={secondaryStore}
              tone="secondary"
            />
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

export function LetterInvitePageView({ match }) {
  usePageTitle("A Raven Letter Is Waiting");
  const { isNarrow, isMobile, height } = useViewport();
  const letterToken = match?.params?.letterToken;
  const [letterInvite, setLetterInvite] = useState(null);
  const [loadStatus, setLoadStatus] = useState("loading");
  const isAndroid = getVisitorStorePlatform() === "android";
  const primaryStoreUrl = isAndroid ? GOOGLE_PLAY_URL : APP_STORE_URL;
  const primaryStoreLabel = isAndroid ? "Google Play" : "App Store";
  const primaryStore = isAndroid ? "google" : "apple";
  const primaryStoreEyebrow = isAndroid ? "Get it on" : "Download on the";
  const secondaryStoreUrl = isAndroid ? APP_STORE_URL : GOOGLE_PLAY_URL;
  const secondaryStoreLabel = isAndroid ? "App Store" : "Google Play";
  const secondaryStore = isAndroid ? "apple" : "google";
  const secondaryStoreEyebrow = isAndroid ? "Download on the" : "Get it on";
  const inviteMinHeight = Math.max(isMobile ? 640 : 620, height - 108);
  const didInviteFail = loadStatus === "error";
  const isInviteLoading = loadStatus === "loading";
  const senderName = letterInvite?.senderName || "";
  const senderImageUrl = letterInvite?.senderImageUrl || "";
  const letterHeader = isInviteLoading
    ? "Loading your letter..."
    : didInviteFail
      ? "This letter link is unavailable"
      : letterInvite?.header || LETTER_INVITE_DEFAULT_HEADER;
  const previewParagraphs = getInviteParagraphs(
    isInviteLoading
      ? LETTER_INVITE_LOADING_MESSAGE
      : didInviteFail
        ? LETTER_INVITE_ERROR_MESSAGE
        : letterInvite?.message
  );
  const inviteFiles = letterInvite?.files || [];
  const hasFiles = inviteFiles.length > 0;
  const inviteTextClipStyle = getInviteTextClipStyle(
    previewParagraphs,
    hasFiles
  );
  const titleText = didInviteFail
    ? "This Raven letter is unavailable."
    : senderName
      ? `${senderName} sent you a Raven.`
      : "Someone sent you a Raven.";
  const lockedText = letterInvite?.hasHiddenMessage
    ? "Read the rest in Raven"
    : "Open it in Raven";
  const kickerText = isInviteLoading
    ? "Loading a private Raven"
    : didInviteFail
      ? "Letter unavailable"
      : "A private letter is waiting";

  useAppleSmartAppBanner();

  useEffect(() => {
    let isCurrent = true;

    if (!letterToken) {
      setLetterInvite(null);
      setLoadStatus("error");
      return undefined;
    }

    setLoadStatus("loading");
    setLetterInvite(null);

    Api.get(`/letters/public_invite/${encodeURIComponent(letterToken)}`, {
      shouldLog: false,
    })
      .then((response) => {
        if (!isCurrent) return;

        const payload = response.letterInvite || response.letter_invite;
        if (!payload) {
          throw new Error("Missing letter invite payload");
        }

        setLetterInvite(normalizeLetterInvite(payload));
        setLoadStatus("loaded");
      })
      .catch(() => {
        if (!isCurrent) return;
        setLetterInvite(null);
        setLoadStatus("error");
      });

    return () => {
      isCurrent = false;
    };
  }, [letterToken]);

  return (
    <PublicShell>
      <section
        style={{
          ...styles.letterInviteHero,
          minHeight: inviteMinHeight,
          ...(isNarrow ? styles.letterInviteHeroNarrow : {}),
        }}
      >
        <div aria-hidden="true" style={styles.letterInviteScene}>
          <SceneBand
            style={{
              ...styles.inviteSceneBandOne,
              ...(isMobile ? styles.inviteSceneBandOneMobile : {}),
            }}
          />
          <SceneBand style={styles.inviteSceneBandTwo} />
          <ColorTrail style={styles.inviteTrailOne} amplitude={5} phase={0.7} />
          <ColorTrail style={styles.inviteTrailTwo} amplitude={4} phase={1.8} />
        </div>

        <div
          style={{
            ...styles.letterInviteContent,
            ...(isNarrow ? styles.letterInviteContentNarrow : {}),
          }}
        >
          <div style={styles.letterInviteCopy}>
            <img
              alt=""
              aria-hidden="true"
              src="/raven-assets/app-icon.png"
              style={styles.inviteAppIcon}
            />
            <p style={styles.kicker}>{kickerText}</p>
            <h1
              style={{
                ...styles.letterInviteTitle,
                ...(isMobile ? styles.letterInviteTitleMobile : {}),
              }}
            >
              {titleText}
            </h1>
            <p
              style={{
                ...styles.letterInviteLead,
                ...(isMobile ? styles.letterInviteLeadMobile : {}),
              }}
            >
              Raven is a slow messaging app where letters travel across a tiny
              map before they can be read. Download the app to unlock this one
              and keep the conversation going.
            </p>

            <div
              style={{
                ...styles.storeLinkStack,
                ...(isMobile ? styles.storeLinkStackMobile : {}),
              }}
            >
              <StoreLink
                eyebrow={primaryStoreEyebrow}
                href={primaryStoreUrl}
                label={primaryStoreLabel}
                store={primaryStore}
              />
              <StoreLink
                eyebrow={secondaryStoreEyebrow}
                href={secondaryStoreUrl}
                label={secondaryStoreLabel}
                store={secondaryStore}
                tone="secondary"
              />
            </div>
          </div>

          <div
            style={{
              ...styles.inviteLetterStage,
              ...(isNarrow ? styles.inviteLetterStageNarrow : {}),
            }}
          >
            <RavenSprite
              size={isMobile ? 128 : 172}
              style={styles.inviteRavenSprite}
              amplitude={isMobile ? 5 : 7}
            />
            <article
              aria-label="Partial Raven letter"
              style={{
                ...styles.inviteLetterPaper,
                ...(hasFiles ? styles.inviteLetterPaperWithFiles : {}),
                ...(isMobile ? styles.inviteLetterPaperMobile : {}),
                ...(hasFiles && isMobile
                  ? styles.inviteLetterPaperWithFilesMobile
                  : {}),
              }}
            >
              <div style={styles.inviteLetterHeader}>
                <div style={styles.inviteLetterSender}>
                  <SenderAvatar
                    imageUrl={senderImageUrl}
                    name={senderName || "A Raven user"}
                    size={42}
                    style={styles.inviteLetterAvatar}
                  />
                  <div style={styles.inviteLetterSenderText}>
                    <span style={styles.inviteLetterSenderLabel}>From</span>
                    <span style={styles.inviteLetterSenderName}>
                      {senderName || "A Raven user"}
                    </span>
                  </div>
                </div>
              </div>
              <div
                style={{
                  ...styles.inviteLetterClip,
                  ...(hasFiles ? styles.inviteLetterClipWithFiles : {}),
                  ...inviteTextClipStyle,
                }}
              >
                <p style={styles.inviteLetterGreeting}>{letterHeader}</p>
                {previewParagraphs.map((paragraph, idx) => (
                  <p key={`${idx}-${paragraph}`} style={styles.inviteLetterText}>
                    {paragraph}
                  </p>
                ))}
                <div aria-hidden="true" style={styles.inviteLetterFade} />
              </div>
              <LetterInviteFiles files={inviteFiles} />
              <a
                aria-label={`${lockedText} on ${primaryStoreLabel}`}
                href={primaryStoreUrl}
                rel="noopener noreferrer"
                style={styles.inviteLetterLocked}
                target="_blank"
              >
                {lockedText}
              </a>
            </article>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

const styles = {
  publicPage: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    color: "#f8fafc",
    background: "#101114",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
    letterSpacing: 0,
    overflowY: "auto",
  },
  policyPage: {
    background: "radial-gradient(circle at 84% 4%, rgba(125, 215, 207, 0.14), transparent 32%), #101114",
  },
  hero: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    background: LANDING_GRADIENT,
    borderBottom: "1px solid rgba(255,255,255,0.13)",
  },
  heroNarrow: {
    alignItems: "flex-end",
    paddingTop: 260,
  },
  heroMobile: {
    paddingTop: 64,
  },
  heroScene: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
  },
  sceneBand: {
    position: "absolute",
    height: 108,
    borderRadius: 28,
    opacity: 0.18,
    transform: "rotate(-16deg)",
  },
  sceneBandOne: {
    width: 520,
    top: 44,
    right: -150,
    backgroundColor: "#ff7ab6",
  },
  sceneBandTwo: {
    width: 420,
    bottom: 34,
    left: -160,
    backgroundColor: "#28f0c7",
  },
  colorTrail: {
    position: "absolute",
    height: 5,
    borderRadius: 3,
    willChange: "transform",
  },
  trailOne: {
    width: 330,
    top: "23%",
    left: "7%",
    backgroundColor: "rgba(255, 213, 94, 0.78)",
  },
  trailTwo: {
    width: 260,
    top: "33%",
    right: "10%",
    backgroundColor: "rgba(103, 232, 249, 0.72)",
  },
  trailThree: {
    width: 240,
    bottom: "24%",
    left: "18%",
    backgroundColor: "rgba(135, 34, 242, 0.76)",
  },
  heroRaven: {
    position: "absolute",
    right: "9%",
    top: "23%",
  },
  heroRavenNarrow: {
    right: "50%",
    top: 64,
    marginRight: -130,
  },
  heroRavenMobile: {
    top: 24,
    marginRight: -78,
  },
  heroContent: {
    position: "relative",
    zIndex: 1,
    width: "min(580px, calc(100% - 72px))",
    marginLeft: 72,
    padding: "48px 0",
  },
  heroContentNarrow: {
    width: "min(580px, calc(100vw - 48px))",
    marginLeft: 20,
    paddingBottom: 66,
  },
  kicker: {
    margin: "0 0 14px",
    color: "#7dd7cf",
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.45,
    textTransform: "uppercase",
  },
  heroTitle: {
    margin: 0,
    color: "#ffffff",
    fontFamily: "Georgia, Times New Roman, serif",
    fontSize: 84,
    fontWeight: 800,
    lineHeight: 0.98,
    letterSpacing: 0,
    textShadow: "0 4px 14px rgba(0,0,0,0.26)",
  },
  heroTitleMobile: {
    fontSize: 50,
  },
  heroSubtitle: {
    margin: "14px 0 0",
    color: "#67e8f9",
    fontSize: 26,
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: 0,
    textShadow: "0 2px 10px rgba(0,0,0,0.22)",
  },
  heroSubtitleMobile: {
    fontSize: 20,
  },
  heroCopy: {
    maxWidth: 520,
    margin: "18px 0 0",
    color: "#eef1f8",
    fontSize: 20,
    lineHeight: 1.55,
    textShadow: "0 1px 8px rgba(0,0,0,0.22)",
  },
  heroCopyMobile: {
    fontSize: 16,
    overflowWrap: "anywhere",
  },
  landingLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 46,
    border: "1px solid rgba(255, 255, 255, 0.24)",
    borderRadius: 8,
    padding: "0 19px",
    fontSize: 15,
    fontWeight: 800,
    textDecoration: "none",
  },
  primaryLink: {
    background: "rgba(255,255,255,0.94)",
    color: "#17113a",
    boxShadow: "0 8px 18px rgba(0,0,0,0.18)",
  },
  secondaryLink: {
    background: "rgba(255,255,255,0.11)",
    color: "#ffffff",
  },
  section: {
    width: "min(1180px, calc(100% - 48px))",
    margin: "0 auto",
    padding: "70px 0",
  },
  sectionMobile: {
    width: "calc(100vw - 48px)",
    padding: "48px 0",
  },
  videoGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(280px, 0.92fr) minmax(260px, 0.58fr)",
    gap: 52,
    alignItems: "center",
    padding: "8px 0 18px",
  },
  videoCopy: {
    maxWidth: 560,
  },
  videoText: {
    maxWidth: 500,
    margin: "18px 0 0",
    color: "#c9d3df",
    fontSize: 17,
    lineHeight: 1.68,
  },
  videoFrame: {
    position: "relative",
    justifySelf: "center",
    width: "min(360px, 100%)",
    aspectRatio: "9 / 16",
    overflow: "hidden",
    borderRadius: 8,
    background:
      "linear-gradient(180deg, rgba(101, 49, 215, 0.96), rgba(21, 151, 214, 0.82))",
    boxShadow: "0 28px 55px rgba(0, 0, 0, 0.32)",
  },
  videoFrameMobile: {
    width: "min(340px, 100%)",
  },
  videoIframe: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    border: 0,
  },
  videoPlaceholder: {
    appearance: "none",
    WebkitAppearance: "none",
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 18,
    width: "100%",
    height: "100%",
    border: 0,
    padding: 28,
    background:
      "radial-gradient(circle at 35% 26%, rgba(255, 255, 255, 0.18), transparent 36%), linear-gradient(145deg, rgba(16, 17, 20, 0.2), rgba(16, 17, 20, 0.62))",
    cursor: "pointer",
  },
  videoPlaceholderIcon: {
    width: 94,
    height: 94,
    borderRadius: 22,
    objectFit: "cover",
    boxShadow: "0 16px 28px rgba(0, 0, 0, 0.28)",
  },
  videoPlayButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 62,
    height: 62,
    borderRadius: 31,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    boxShadow: "0 12px 28px rgba(0, 0, 0, 0.26)",
  },
  videoPlayTriangle: {
    width: 0,
    height: 0,
    marginLeft: 5,
    borderTop: "13px solid transparent",
    borderBottom: "13px solid transparent",
    borderLeft: "18px solid #17113a",
  },
  videoPlaceholderTitle: {
    color: "#ffffff",
    fontSize: 18,
    fontWeight: 900,
    letterSpacing: 0,
  },
  aboutGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(280px, 0.88fr) minmax(0, 1.12fr)",
    gap: 46,
    alignItems: "start",
  },
  aboutIntro: {
    maxWidth: 520,
  },
  aboutLead: {
    margin: "0 0 20px",
    color: "#fff4e7",
    fontSize: 32,
    lineHeight: 1.18,
    letterSpacing: 0,
  },
  aboutLeadMobile: {
    fontSize: 26,
  },
  aboutText: {
    margin: "0 0 14px",
    color: "#fff4e7",
    fontSize: 16,
    lineHeight: 1.58,
  },
  aboutHighlightStack: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  aboutHighlightCard: {
    display: "flex",
    alignItems: "center",
    minHeight: 86,
    padding: "14px 16px",
    borderLeft: "3px solid #ffd47d",
    borderRadius: 8,
    backgroundColor: "rgba(255, 255, 255, 0.06)",
  },
  aboutHighlightMark: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 54,
    height: 54,
    borderRadius: 8,
    marginRight: 14,
    flexShrink: 0,
  },
  aboutHighlightDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
  },
  aboutHighlightCopy: {
    minWidth: 0,
  },
  oneColumnGrid: {
    gridTemplateColumns: "1fr",
  },
  sectionTitle: {
    margin: 0,
    color: "#ffffff",
    fontSize: 38,
    lineHeight: 1.08,
    letterSpacing: 0,
  },
  sectionTitleMobile: {
    fontSize: 30,
    overflowWrap: "anywhere",
  },
  featureTitle: {
    margin: "0 0 12px",
    color: "#ffffff",
    fontSize: 21,
    lineHeight: 1.2,
  },
  bodyText: {
    margin: 0,
    color: "#c9d3df",
    fontSize: 16,
    lineHeight: 1.72,
  },
  infoBand: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 30,
    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
  },
  infoBandNarrow: {
    alignItems: "flex-start",
    flexDirection: "column",
  },
  policyLinkGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
  },
  letterInviteHero: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    padding: "42px 0 58px",
    background:
      "radial-gradient(circle at 78% 18%, rgba(103, 232, 249, 0.22), transparent 34%), linear-gradient(135deg, #16113f 0%, #6531d7 56%, #1597d6 100%)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
  },
  letterInviteHeroNarrow: {
    alignItems: "flex-start",
    padding: "24px 0 46px",
  },
  memberInviteHeroMobile: {
    padding: "28px 0 44px",
  },
  letterInviteScene: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
  },
  inviteSceneBandOne: {
    width: 470,
    top: 54,
    right: -130,
    backgroundColor: "#8722F2",
  },
  inviteSceneBandOneMobile: {
    width: 300,
    top: 24,
    right: -135,
  },
  inviteSceneBandTwo: {
    width: 380,
    bottom: 42,
    left: -145,
    backgroundColor: "#67e8f9",
  },
  inviteTrailOne: {
    width: 250,
    top: "24%",
    left: "7%",
    backgroundColor: "rgba(135, 34, 242, 0.74)",
  },
  inviteTrailTwo: {
    width: 220,
    bottom: "19%",
    right: "12%",
    backgroundColor: "rgba(103, 232, 249, 0.68)",
  },
  letterInviteContent: {
    position: "relative",
    zIndex: 1,
    display: "grid",
    gridTemplateColumns: "minmax(300px, 0.9fr) minmax(320px, 0.75fr)",
    alignItems: "center",
    gap: 52,
    width: "min(1120px, calc(100% - 56px))",
    margin: "0 auto",
  },
  letterInviteContentNarrow: {
    gridTemplateColumns: "1fr",
    gap: 34,
    width: "100%",
    padding: "0 18px",
    boxSizing: "border-box",
  },
  memberInviteContent: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "min(720px, calc(100% - 40px))",
    margin: "0 auto",
    textAlign: "center",
  },
  memberInviteRaven: {
    marginBottom: 4,
  },
  memberInviteTitle: {
    maxWidth: 700,
    margin: 0,
    color: "#ffffff",
    fontFamily: "Georgia, Times New Roman, serif",
    fontSize: 62,
    fontWeight: 800,
    lineHeight: 1.02,
    letterSpacing: 0,
    textShadow: "0 4px 16px rgba(0, 0, 0, 0.28)",
  },
  memberInviteTitleMobile: {
    fontSize: 40,
    lineHeight: 1.06,
  },
  memberInviteDescription: {
    margin: "22px 0 0",
    color: "#67e8f9",
    fontSize: 24,
    fontWeight: 800,
    lineHeight: 1.3,
  },
  memberInviteBody: {
    maxWidth: 570,
    margin: "16px 0 0",
    color: "#eef1f8",
    fontSize: 18,
    lineHeight: 1.58,
    textShadow: "0 1px 8px rgba(0, 0, 0, 0.2)",
  },
  memberInviteBodyMobile: {
    fontSize: 16,
  },
  memberInviteStoreLinks: {
    justifyContent: "center",
  },
  letterInviteCopy: {
    width: "100%",
    maxWidth: 580,
    minWidth: 0,
  },
  inviteAppIcon: {
    width: 68,
    height: 68,
    marginBottom: 24,
    borderRadius: 16,
    objectFit: "cover",
    boxShadow: "0 16px 30px rgba(22, 17, 63, 0.34)",
  },
  letterInviteTitle: {
    margin: 0,
    maxWidth: "100%",
    color: "#ffffff",
    fontFamily: "Georgia, Times New Roman, serif",
    fontSize: 64,
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: 0,
    overflowWrap: "break-word",
    textShadow: "0 4px 16px rgba(0, 0, 0, 0.28)",
  },
  letterInviteTitleMobile: {
    fontSize: 39,
    lineHeight: 1.05,
  },
  letterInviteLead: {
    width: "100%",
    maxWidth: 540,
    margin: "22px 0 0",
    color: "#eef1f8",
    fontSize: 20,
    lineHeight: 1.54,
    textShadow: "0 1px 8px rgba(0, 0, 0, 0.2)",
  },
  letterInviteLeadMobile: {
    fontSize: 17,
  },
  storeLinkStack: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 34,
  },
  storeLinkStackMobile: {
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
  },
  storeLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    minWidth: 184,
    minHeight: 60,
    maxWidth: "100%",
    borderRadius: 8,
    padding: "9px 16px",
    textDecoration: "none",
    boxSizing: "border-box",
  },
  primaryStoreLink: {
    color: "#ffffff",
    background:
      "linear-gradient(135deg, rgba(23, 17, 58, 0.98), rgba(79, 20, 143, 0.96))",
    border: "1px solid rgba(103, 232, 249, 0.34)",
    boxShadow: "0 16px 30px rgba(23, 17, 58, 0.28)",
  },
  secondaryStoreLink: {
    color: "#ffffff",
    backgroundColor: "rgba(23, 17, 58, 0.68)",
    border: "1px solid rgba(255, 255, 255, 0.24)",
  },
  storeIconWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 34,
    flexShrink: 0,
  },
  storeIconSvg: {
    display: "block",
    width: "100%",
    height: "100%",
  },
  storeLinkText: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0,
  },
  storeLinkEyebrow: {
    fontSize: 10,
    fontWeight: 800,
    lineHeight: 1.05,
    textTransform: "uppercase",
    opacity: 0.86,
  },
  storeLinkLabel: {
    marginTop: 3,
    fontSize: 21,
    fontWeight: 900,
    lineHeight: 1.05,
    letterSpacing: 0,
  },
  senderAvatar: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    color: "rgba(255, 255, 255, 0.94)",
    boxSizing: "border-box",
    flexShrink: 0,
  },
  senderAvatarImage: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  senderAvatarInitial: {
    color: "rgba(255, 255, 255, 0.94)",
    fontWeight: 900,
    lineHeight: 1,
    textShadow: "0 0 4px rgba(0, 0, 0, 0.48)",
  },
  inviteLetterStage: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 470,
  },
  inviteLetterStageNarrow: {
    width: "100%",
    minHeight: 390,
    minWidth: 0,
  },
  inviteRavenSprite: {
    position: "absolute",
    right: 0,
    top: 12,
    zIndex: 1,
  },
  inviteLetterPaper: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: "min(370px, 100%)",
    minHeight: 470,
    padding: "30px 30px 24px",
    overflow: "hidden",
    backgroundColor: "#f7f5ff",
    border: "1px solid rgba(101, 49, 215, 0.2)",
    borderRadius: 8,
    boxShadow: "0 32px 54px rgba(0, 0, 0, 0.34)",
    transform: "rotate(-2.4deg)",
    boxSizing: "border-box",
  },
  inviteLetterPaperWithFiles: {
    minHeight: 560,
  },
  inviteLetterPaperMobile: {
    minHeight: 420,
    padding: "25px 23px 22px",
    transform: "rotate(-1.2deg)",
  },
  inviteLetterPaperWithFilesMobile: {
    minHeight: 520,
  },
  inviteLetterHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingBottom: 18,
    borderBottom: "1px solid rgba(101, 49, 215, 0.16)",
  },
  inviteLetterSender: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    minWidth: 0,
  },
  inviteLetterAvatar: {
    border: "1px solid rgba(255, 255, 255, 0.64)",
    boxShadow: "0 7px 13px rgba(23, 17, 58, 0.2)",
  },
  inviteLetterSenderText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  inviteLetterSenderLabel: {
    color: "#746b9c",
    fontSize: 10,
    fontWeight: 900,
    lineHeight: 1.1,
    textTransform: "uppercase",
  },
  inviteLetterSenderName: {
    maxWidth: 150,
    marginTop: 3,
    overflow: "hidden",
    color: "#17113a",
    fontSize: 16,
    fontWeight: 900,
    lineHeight: 1.1,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  inviteLetterClip: {
    position: "relative",
    maxHeight: 245,
    overflow: "hidden",
    paddingTop: 26,
    flexShrink: 0,
  },
  inviteLetterClipWithFiles: {
    maxHeight: 175,
  },
  inviteLetterGreeting: {
    margin: "0 0 18px",
    color: "#17113a",
    fontFamily: "Georgia, Times New Roman, serif",
    fontSize: 25,
    fontWeight: 800,
    lineHeight: 1.2,
    overflowWrap: "break-word",
  },
  inviteLetterText: {
    margin: "0 0 17px",
    color: "#2a2550",
    fontFamily: "Georgia, Times New Roman, serif",
    fontSize: 19,
    lineHeight: 1.58,
    overflowWrap: "break-word",
  },
  inviteLetterFade: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 72,
    background:
      "linear-gradient(180deg, rgba(247, 245, 255, 0), rgba(247, 245, 255, 0.42) 52%, #f7f5ff 100%)",
  },
  inviteLetterFiles: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: 14,
    marginBottom: 16,
    flexShrink: 0,
  },
  inviteLetterFile: {
    overflow: "hidden",
    borderRadius: 8,
    border: "1px solid rgba(101, 49, 215, 0.18)",
    backgroundColor: "rgba(255, 255, 255, 0.42)",
  },
  inviteLetterFileLabel: {
    display: "block",
    padding: "8px 10px 6px",
    color: "#746b9c",
    fontSize: 10,
    fontWeight: 900,
    lineHeight: 1,
    textTransform: "uppercase",
  },
  inviteLetterMedia: {
    display: "block",
    width: "100%",
    maxHeight: 210,
    objectFit: "cover",
    backgroundColor: "#17113a",
  },
  inviteLetterAudioTile: {
    display: "flex",
    alignItems: "center",
    minHeight: 56,
    padding: "0 10px 10px",
    boxSizing: "border-box",
  },
  inviteLetterAudio: {
    width: "100%",
  },
  inviteLetterFileLink: {
    display: "block",
    padding: 12,
    color: "#4F148F",
    fontSize: 13,
    fontWeight: 900,
    textDecoration: "none",
  },
  inviteLetterLocked: {
    marginTop: "auto",
    minHeight: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    background:
      "linear-gradient(135deg, #4F148F 0%, #1597d6 100%)",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 900,
    textAlign: "center",
    textDecoration: "none",
    textTransform: "uppercase",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    padding: "28px 42px 34px",
    color: "#8f9cab",
    borderTop: "1px solid rgba(255, 255, 255, 0.09)",
  },
  footerMobile: {
    flexDirection: "column",
    padding: "26px 24px 32px",
  },
  footerLink: {
    color: "#dce7f2",
    textDecoration: "none",
  },
  policyHero: {
    width: "min(980px, calc(100% - 48px))",
    margin: "0 auto",
    padding: "82px 0 42px",
  },
  policyHeroMobile: {
    width: "calc(100% - 36px)",
    padding: "56px 0 30px",
  },
  policyTitle: {
    margin: 0,
    color: "#ffffff",
    fontFamily: "Georgia, Times New Roman, serif",
    fontSize: 58,
    fontWeight: 800,
    lineHeight: 0.98,
    letterSpacing: 0,
  },
  policyTitleMobile: {
    fontSize: 40,
  },
  policyIntro: {
    maxWidth: 820,
    margin: "22px 0 0",
    color: "#c9d3df",
    fontSize: 18,
    lineHeight: 1.72,
  },
  policyIntroMobile: {
    fontSize: 16,
  },
  policyContent: {
    width: "min(980px, calc(100% - 48px))",
    margin: "0 auto",
    padding: "12px 0 78px",
  },
  policyContentMobile: {
    width: "calc(100% - 36px)",
  },
  policySection: {
    display: "grid",
    gridTemplateColumns: "72px minmax(0, 1fr)",
    gap: 24,
    padding: "30px 0",
    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
  },
  policySectionMobile: {
    gridTemplateColumns: "1fr",
    gap: 10,
    padding: "26px 0",
  },
  policySectionLabel: {
    color: "#f1c96b",
    fontSize: 13,
    fontWeight: 900,
  },
  policySectionTitle: {
    margin: "0 0 12px",
    color: "#ffffff",
    fontSize: 26,
    lineHeight: 1.2,
    letterSpacing: 0,
  },
  policySubheading: {
    margin: "26px 0 8px",
    color: "#ffffff",
    fontSize: 18,
    lineHeight: 1.3,
  },
  policyText: {
    margin: "0 0 15px",
    color: "#c9d3df",
    fontSize: 16,
    lineHeight: 1.72,
  },
  bulletList: {
    margin: "10px 0 18px",
    paddingLeft: 20,
  },
  policyListItem: {
    marginBottom: 8,
    color: "#c9d3df",
    fontSize: 16,
    lineHeight: 1.72,
  },
  inlineLink: {
    color: "#9ee7df",
    fontWeight: 800,
  },
  contactPanel: {
    marginTop: 22,
    padding: 26,
    border: "1px solid rgba(158, 231, 223, 0.26)",
    borderRadius: 8,
    background: "rgba(125, 215, 207, 0.08)",
  },
  contactPanelTitle: {
    margin: "0 0 10px",
    color: "#ffffff",
    fontSize: 24,
  },
};
